import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

const isRequestCanceled = (err) => err?.code === "ERR_CANCELED" || err?.name === "CanceledError";

const formatEventType = (value) => {
    if (!value) return "—";

    return value
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
};

function SMS() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchTemplates = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await axiosPrivate.get("/sms/templates", {
                    headers: auth?.accessToken
                        ? { Authorization: "Bearer " + auth.accessToken }
                        : undefined,
                    signal: controller.signal
                });

                if (isMounted) {
                    setTemplates(Array.isArray(response.data) ? response.data : []);
                }
            } catch (err) {
                if (isMounted && !isRequestCanceled(err)) {
                    setError("Failed to load SMS templates.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (auth?.accessToken) {
            fetchTemplates();
        } else {
            setLoading(false);
            setTemplates([]);
        }

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [axiosPrivate, auth?.accessToken]);

    const renderTableBody = () => {
        if (templates.length === 0) {
            return (
                <tr>
                    <td
                        colSpan="6"
                        className="px-6 py-6 text-center text-sm text-gray-500"
                    >
                        No templates available.
                    </td>
                </tr>
            );
        }

        return templates.map((template) => {
            const templateId = template.id ?? template.template_id;

            return (
                <tr key={templateId ?? template.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {template.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 align-top">
                        <div className="max-w-xl whitespace-pre-wrap break-words">{template.body}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {formatEventType(template.event_type)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {template.is_active ? "Yes" : "No"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {template.is_default ? "Yes" : "No"}
                </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {template.updated_at
                            ? new Date(template.updated_at).toLocaleString()
                            : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <button
                            type="button"
                            onClick={() => navigate(`/edit-template/${templateId}`)}
                            disabled={!templateId}
                            className="rounded-lg border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Edit
                        </button>
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-slate-100 p-6">
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">SMS Templates</h2>
                        <p className="text-sm text-gray-500">Manage template content and status.</p>
                    </div>
                    <button
                        onClick={() => navigate("/add-template")}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                    >
                        Add Template
                    </button>
                </div>
            </div>

            {loading && (
                <div className="py-10 text-center text-sm text-gray-500">Loading templates…</div>
            )}

            {error && !loading && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                    {error}
                </div>
            )}

            {!loading && !error && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Body
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Event
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Active
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Default
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Updated
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">{renderTableBody()}</tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SMS;
